import { Box, Heading, Text } from '@fuegokit/react';
import React, { useMemo } from 'react';

import { usePersonalConfiguration } from '../../screens/PersonalConfigurationProvider';
import { PersonalConfiguration } from '../../types';
import { SwitchElement } from '../reusable/SwitchElement';
import { ConnectionStatus } from './ConnectionStatus';

const labelsMapping: Record<string, { label: string; explanation?: string }> = {
  hideTimeEntryWithoutIssueKey: {
    label: 'Ignore time entries where issue key was not detected',
  },
  syncByCron: {
    label: 'Synchronize automatically',
    explanation:
      'Toggl app will try synchronizing the external most recent time entries from Toggl Track with an issue based on the detected issue key.',
  },
  alwaysDisplayOptionsInSidebar: {
    label: 'Issue tracking options are always displayed in glance view',
  },
  hideApiLimitsWarningMessage: {
    label: 'Hide Toggl requests limitations message in glance view.',
    explanation:
      'This may result in quickly exceeding the limits and blocking the application for the next hour.',
  },
};

export function PersonalSettings() {
  const { config, updateConfig } = usePersonalConfiguration();

  const switchesValues = useMemo(
    () => ({
      hideTimeEntryWithoutIssueKey: config.hideTimeEntryWithoutIssueKey,
      syncByCron: config.syncByCron,
      alwaysDisplayOptionsInSidebar: config.alwaysDisplayOptionsInSidebar,
      hideApiLimitsWarningMessage: config.hideApiLimitsWarningMessage,
    }),
    [
      config.hideTimeEntryWithoutIssueKey,
      config.syncByCron,
      config.alwaysDisplayOptionsInSidebar,
      config.hideApiLimitsWarningMessage,
    ]
  );

  const onToggle = (id: string) =>
    void updateConfig({
      [id]: !config[id as keyof PersonalConfiguration],
    });

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '2',
      }}
    >
      <ConnectionStatus />
      <Heading
        variant="h600"
        sx={{
          fontWeight: 'bold',
        }}
      >
        Personal settings
      </Heading>
      <Text color="text.default">
        All of those settings will apply only to your Jira instance
      </Text>
      <Box>
        {Object.entries(switchesValues).map(([id, value]) => (
          <SwitchElement
            key={id}
            label={labelsMapping[id].label}
            id={id}
            value={value as boolean}
            onToggle={() => onToggle(id)}
            explanation={labelsMapping[id]?.explanation}
          />
        ))}
      </Box>
    </Box>
  );
}
